import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useConflictReviewStore } from './conflict-review-store';
import type { ProjectScope } from './project-scope';
import { sendProjectWriteBatchToAuthority } from './project-write-authority-api';
import {
    listPendingProjectWrites,
    markProjectWriteResult,
    subscribeToProjectWriteOutboxChanges,
} from './project-write-outbox';

type UseProjectWriteDispatcherOptions = {
    enabled: boolean;
    scope: ProjectScope | null | undefined;
    userId: string | null | undefined;
};

type DispatcherConfig = {
    enabled: boolean;
    scope: ProjectScope | null;
    userId: string | null;
};

type MutableRef<T> = {
    current: T;
};

type DrainRuntime = {
    isDrainingRef: MutableRef<boolean>;
    shouldDrainAgainRef: MutableRef<boolean>;
    latestConfigRef: MutableRef<DispatcherConfig>;
    refresh: (scope: ProjectScope | null | undefined) => Promise<void>;
    clear: () => void;
    isLatestScope: (capturedScope: ProjectScope) => boolean;
    scheduleRetry: () => void;
};

async function markOperationsRetryable(
    operations: Awaited<ReturnType<typeof listPendingProjectWrites>>,
    reason: string,
): Promise<void> {
    await Promise.all(
        operations.map((operation) => markProjectWriteResult({
            operationId: operation.id,
            status: 'retryable',
            reason,
        })),
    );
}

async function submitOperations(
    capturedScope: ProjectScope,
    operations: Awaited<ReturnType<typeof listPendingProjectWrites>>,
    scheduleRetry: () => void,
): Promise<void> {
    const response = await sendProjectWriteBatchToAuthority({
        orgId: capturedScope.orgId,
        projectId: capturedScope.projectId,
        operations,
    });

    await Promise.all(response.results.map((result) => markProjectWriteResult(result)));
    if (response.results.some((result) => result.status === 'retryable')) {
        scheduleRetry();
    }
}

async function drainCurrentScope(runtime: DrainRuntime): Promise<void> {
    const config = runtime.latestConfigRef.current;
    const capturedScope = config.scope;

    if (!config.enabled || !capturedScope) {
        runtime.clear();
        return;
    }

    if (!config.userId) {
        await runtime.refresh(capturedScope);
        return;
    }

    const operations = await listPendingProjectWrites(capturedScope);
    if (operations.length === 0) {
        if (runtime.isLatestScope(capturedScope)) {
            await runtime.refresh(capturedScope);
        }
        return;
    }

    try {
        await submitOperations(capturedScope, operations, runtime.scheduleRetry);
    } catch (error) {
        const reason = error instanceof Error ? error.message : 'temporary callable failure';
        await markOperationsRetryable(operations, reason);
        runtime.scheduleRetry();
    }

    if (runtime.isLatestScope(capturedScope)) {
        await runtime.refresh(capturedScope);
    }
}

async function drainUntilIdle(runtime: DrainRuntime): Promise<void> {
    try {
        do {
            runtime.shouldDrainAgainRef.current = false;
            await drainCurrentScope(runtime);
        } while (runtime.shouldDrainAgainRef.current);
    } finally {
        runtime.isDrainingRef.current = false;
    }
}

function startDrain(runtime: DrainRuntime): void {
    if (runtime.isDrainingRef.current) {
        runtime.shouldDrainAgainRef.current = true;
        return;
    }

    runtime.isDrainingRef.current = true;
    void drainUntilIdle(runtime);
}

function useProjectWriteDrainEffect(input: {
    activeScope: ProjectScope | null;
    clear: () => void;
    enabled: boolean;
    isDrainingRef: MutableRef<boolean>;
    isLatestScope: (capturedScope: ProjectScope) => boolean;
    latestConfigRef: MutableRef<DispatcherConfig>;
    refresh: (scope: ProjectScope | null | undefined) => Promise<void>;
    retryTimeoutRef: MutableRef<number | null>;
    shouldDrainAgainRef: MutableRef<boolean>;
    userId: string | null | undefined;
}): void {
    const {
        activeScope,
        clear,
        enabled,
        isDrainingRef,
        isLatestScope,
        latestConfigRef,
        refresh,
        retryTimeoutRef,
        shouldDrainAgainRef,
        userId,
    } = input;

    useEffect(() => {
        if (!enabled || !activeScope) {
            clear();
            return;
        }

        if (!userId) {
            void refresh(activeScope);
            return;
        }

        function clearRetry() {
            if (retryTimeoutRef.current !== null) {
                window.clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        }

        function scheduleRetry() {
            if (retryTimeoutRef.current !== null) return;
            retryTimeoutRef.current = window.setTimeout(() => {
                retryTimeoutRef.current = null;
                drain();
            }, 1000);
        }

        const runtime: DrainRuntime = {
            isDrainingRef,
            shouldDrainAgainRef,
            latestConfigRef,
            refresh,
            clear,
            isLatestScope,
            scheduleRetry,
        };

        function drain() {
            clearRetry();
            startDrain(runtime);
        }

        drain();
        window.addEventListener('online', drain);

        const unsubscribe = subscribeToProjectWriteOutboxChanges((change) => {
            const latestScope = latestConfigRef.current.scope;
            if (change.scope.orgId === latestScope?.orgId && change.scope.projectId === latestScope.projectId) drain();
        });

        return () => {
            window.removeEventListener('online', drain);
            unsubscribe();
            clearRetry();
        };
    }, [activeScope, clear, enabled, isDrainingRef, isLatestScope, latestConfigRef, refresh, retryTimeoutRef, shouldDrainAgainRef, userId]);
}

export function useProjectWriteDispatcher({
    enabled,
    scope,
    userId,
}: UseProjectWriteDispatcherOptions): void {
    const isDrainingRef = useRef(false);
    const shouldDrainAgainRef = useRef(false);
    const retryTimeoutRef = useRef<number | null>(null);
    const latestConfigRef = useRef<DispatcherConfig>({
        enabled: false,
        scope: null,
        userId: null,
    });
    const refresh = useConflictReviewStore((state) => state.refresh);
    const clear = useConflictReviewStore((state) => state.clear);
    const orgId = scope?.orgId ?? null;
    const projectId = scope?.projectId ?? null;
    const activeScope = useMemo(() => (
        orgId && projectId ? { orgId, projectId } : null
    ), [orgId, projectId]);

    useEffect(() => {
        latestConfigRef.current = {
            enabled,
            scope: activeScope,
            userId: userId ?? null,
        };
    }, [activeScope, enabled, userId]);

    const isLatestScope = useCallback((capturedScope: ProjectScope): boolean => {
        const latestScope = latestConfigRef.current.scope;
        return latestConfigRef.current.enabled
            && latestScope?.orgId === capturedScope.orgId
            && latestScope.projectId === capturedScope.projectId;
    }, []);

    useProjectWriteDrainEffect({
        activeScope,
        clear,
        enabled,
        isDrainingRef,
        isLatestScope,
        latestConfigRef,
        refresh,
        retryTimeoutRef,
        shouldDrainAgainRef,
        userId,
    });
}
